/**
 * Product Chat API Slice (RTK Query)
 * Chat for individual favorite products using the product-chat API.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { apiClient } from '@/auth';
import type { RequestBody } from '@http';

//  Types 

export interface ProductChatSession {
    session_id: string;
    chat_session_id: string;
    active_product_asin: string;
    favorite_products_count: number;
}

export interface ProductChatMessage {
    id: number;
    role: 'user' | 'assistant';
    content: string;
    created_at: string;
}

export interface ProductChatHistoryResponse {
    success: boolean;
    session_id: string;
    chat_session_id: string;
    messages: ProductChatMessage[];
    error: string | null;
}

export interface ProductChatSendResponse {
    success: boolean;
    response: string;
    error: string | null;
}

//  Base Query 

interface BaseQueryArgs {
    url: string;
    method?: 'GET' | 'POST' | 'PUT';
    body?: RequestBody;
    params?: Record<string, unknown>;
}

const apiClientBaseQuery = async ({ url, method = 'GET', body, params }: BaseQueryArgs) => {
    try {
        let response;

        switch (method) {
            case 'GET':
                response = await apiClient.get(url, params);
                break;
            case 'POST':
                response = await apiClient.post(url, body ?? undefined);
                break;
            case 'PUT':
                response = await apiClient.put(url, body ?? undefined);
                break;
            default:
                throw new Error(`Unsupported method: ${method}`);
        }

        return { data: response.data };
    } catch (error: unknown) {
        const apiError = error as { response?: { status?: number; data?: unknown }; message?: string };
        return {
            error: {
                status: apiError.response?.status ?? 500,
                data: apiError.response?.data ?? apiError.message,
            },
        };
    }
};

export const PRODUCT_CHAT_POLLING_INTERVAL = 5000;

/**
 * Product Chat API Slice
 */
export const productChatApi = createApi({
    reducerPath: 'productChatApi',
    baseQuery: apiClientBaseQuery,
    tagTypes: ['ProductChatHistory'],
    endpoints: (builder) => ({
        /**
         * Create a new product chat session
         */
        createProductChatSession: builder.mutation<
            ProductChatSession,
            { niche_id: string; active_product_asin: string }
        >({
            query: (body) => ({
                url: 'product-chat/session',
                method: 'POST',
                body,
            }),
        }),

        /**
         * Get chat history for a product chat session
         */
        getProductChatHistory: builder.query<
            ProductChatHistoryResponse,
            { chatSessionId: string; sessionId: string }
        >({
            query: ({ chatSessionId, sessionId }) => ({
                url: `product-chat/session/${chatSessionId}/history`,
                method: 'GET',
                params: { session_id: sessionId },
            }),
            providesTags: ['ProductChatHistory'],
        }),

        /**
         * Send a message in a product chat session
         */
        sendProductChatMessage: builder.mutation<
            ProductChatSendResponse,
            { chatSessionId: string; sessionId: string; message: string }
        >({
            query: ({ chatSessionId, sessionId, message }) => ({
                url: `product-chat/session/${chatSessionId}/message`,
                method: 'POST',
                body: { session_id: sessionId, message },
            }),
            invalidatesTags: ['ProductChatHistory'],
        }),

        /**
         * Update favorites in a chat session
         */
        updateProductChatFavorites: builder.mutation<
            void,
            { chatSessionId: string; sessionId: string; nicheId: string }
        >({
            query: ({ chatSessionId, sessionId, nicheId }) => ({
                url: `product-chat/session/${chatSessionId}/favorites?niche_id=${encodeURIComponent(nicheId)}`,
                method: 'PUT',
                body: { session_id: sessionId },
            }),
        }),
    }),
});

export const {
    useCreateProductChatSessionMutation,
    useGetProductChatHistoryQuery,
    useSendProductChatMessageMutation,
    useUpdateProductChatFavoritesMutation,
} = productChatApi;
